import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PortfolioMediaTile } from '../../lib/portfolio-tiles';
import type { CategoryAccent } from '../../lib/category-theme';
import { getCategoryIcon } from '../../lib/category-theme';
import PortfolioMediaCard from './PortfolioMediaCard';

const CAROUSEL_PREVIEW_LIMIT = 16;

type Props = {
  title: string;
  slug?: string;
  categoryId: string;
  tiles: PortfolioMediaTile[];
  accent: CategoryAccent;
  imgSrc: (url?: string) => string;
  onOpenTile: (tile: PortfolioMediaTile) => void;
  compact?: boolean;
};

export default function PortfolioCarouselRow({
  title,
  slug,
  categoryId,
  tiles,
  accent,
  imgSrc,
  onOpenTile,
  compact,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const Icon = getCategoryIcon(slug, title);
  const previewTiles = tiles.slice(0, CAROUSEL_PREVIEW_LIMIT);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [previewTiles.length, updateScrollState]);

  const scrollNext = () => {
    scrollRef.current?.scrollBy({ left: scrollRef.current.clientWidth * 0.85, behavior: 'smooth' });
  };

  const scrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -(scrollRef.current.clientWidth * 0.85), behavior: 'smooth' });
  };

  const navButtonClass =
    'absolute top-[38%] z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-zinc-900/90 text-white shadow-xl backdrop-blur-sm transition hover:border-primary-400 hover:bg-primary-500 md:opacity-0 md:group-hover/row:opacity-100';

  if (tiles.length === 0) return null;

  return (
    <div className={compact ? 'min-w-0' : 'mb-10 md:mb-12'}>
      <div className="mb-3 flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${accent.iconBg} text-white shadow-lg`}>
            <Icon className="h-4 w-4" />
          </span>
          <h3 className={`truncate text-base font-black md:text-lg ${accent.text}`}>{title}</h3>
          <span className="hidden text-xs font-semibold text-zinc-500 sm:inline">
            {tiles.length} média{tiles.length !== 1 ? 's' : ''}
          </span>
        </div>
        <Link
          to={`/portfolio?category=${encodeURIComponent(categoryId)}`}
          className={`shrink-0 text-xs font-bold uppercase tracking-wider ${accent.link}`}
        >
          Voir tout →
        </Link>
      </div>

      <div className="relative group/row">
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scroll-smooth px-4 pb-2 snap-x snap-mandatory sm:gap-4 sm:px-6 lg:px-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {previewTiles.map((tile) => (
            <PortfolioMediaCard
              key={tile.id}
              tile={tile}
              imageUrl={imgSrc(tile.imageUrl)}
              accent={accent}
              onOpen={() => onOpenTile(tile)}
              compact={compact}
            />
          ))}
        </div>

        {canScrollLeft && (
          <button
            type="button"
            onClick={scrollPrev}
            aria-label={`Précédent — ${title}`}
            className={`${navButtonClass} left-2 sm:left-4 lg:left-6`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            onClick={scrollNext}
            aria-label={`Suivant — ${title}`}
            className={`${navButtonClass} right-2 sm:right-4 lg:right-6`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
